<template>
  <div class="help-root">

    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">hearing</span>
      <p>
        Registra el examen otorrinolaringológico del oído: otoscopia, acumetría,
        síntomas auditivos, audiometría tonal e impedanciometría con reflejos
        estapediales.
      </p>
    </div>

    <!-- Modos de trabajo -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">toggle_on</span>
        Modos de trabajo
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in modos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pestañas -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">tab</span>
        Pestañas del examen
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="tab in tabs" :key="tab.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ tab.icon }}</span>
          <div>
            <p class="help-card-title">{{ tab.titulo }}</p>
            <p class="help-card-desc">{{ tab.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Otoscopia y acumetría -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">search</span>
        Otoscopia y acumetría
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in otoscopia" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Síntomas -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">sick</span>
        Síntomas registrados
      </h3>
      <div class="help-estados" style="margin-bottom:0.5rem">
        <span class="help-estado-badge" style="background:#f8d7da;color:#721c24">Acúfenos</span>
        <span class="help-estado-badge" style="background:#f8d7da;color:#721c24">Vértigo</span>
        <span class="help-estado-badge" style="background:#f8d7da;color:#721c24">Hipoacusia</span>
        <span class="help-estado-badge" style="background:#f8d7da;color:#721c24">Otalgia</span>
        <span class="help-estado-badge" style="background:#f8d7da;color:#721c24">Otorrea</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem">
        Al activar un síntoma con el interruptor aparecen campos adicionales
        para detallar su tipo o características. Los síntomas activos se
        resumen como etiquetas al pie de la sección.
      </p>
    </section>

    <!-- Audiometría -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">graphic_eq</span>
        Audiometría tonal
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in audiometria" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
      <div class="help-estados" style="margin-top:0.5rem">
        <span class="help-estado-badge" style="background:#d4edda;color:#155724">Normal ≤ 25 dB</span>
        <span class="help-estado-badge" style="background:#fff3cd;color:#856404">Leve 26–40 dB</span>
        <span class="help-estado-badge" style="background:#fde5cc;color:#7d3800">Moderada 41–60 dB</span>
        <span class="help-estado-badge" style="background:#f8d7da;color:#721c24">Severa 61–80 dB</span>
        <span class="help-estado-badge" style="background:#343a40;color:#fff">Profunda > 80 dB</span>
      </div>
    </section>

    <!-- Impedanciometría -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">waves</span>
        Impedanciometría / Timpanometría
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in impedanciometria" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
      <div class="help-estados" style="margin-top:0.5rem">
        <span class="help-estado-badge" style="background:#d4edda;color:#155724">A — Normal</span>
        <span class="help-estado-badge" style="background:#cce5ff;color:#004085">As — Rigidez</span>
        <span class="help-estado-badge" style="background:#e2d9f3;color:#4a235a">Ad — Hipercompliance</span>
        <span class="help-estado-badge" style="background:#f8d7da;color:#721c24">B — Derrame</span>
        <span class="help-estado-badge" style="background:#fff3cd;color:#856404">C — Presión negativa</span>
      </div>
    </section>

    <!-- Acciones -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">touch_app</span>
        Acciones disponibles
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="a in acciones" :key="a.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: a.color }">{{ a.icon }}</span>
          <div>
            <p class="help-card-title">{{ a.titulo }}</p>
            <p class="help-card-desc">{{ a.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Restricción -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">lock</span>
        Restricciones por estado de la atención
      </h3>
      <div class="help-estados" style="margin-bottom:0.5rem">
        <span class="help-estado-badge" style="background:#fff3cd;color:#856404">PENDIENTE</span>
        <span class="help-estado-badge" style="background:#cce5ff;color:#004085">PROCESO</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem">
        El Modo Edición y los botones Guardar y Limpiar solo están habilitados
        en estado <strong>PENDIENTE</strong> o <strong>PROCESO</strong>. En
        otros estados el componente abre en <strong>Modo Consulta</strong>.
      </p>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        El <strong>PTA</strong> (Pure Tone Average) se calcula automáticamente
        al ingresar los umbrales de 500, 1000, 2000 y 4000 Hz, y se clasifica
        según los criterios de la OMS. Puedes agregar registros separados para
        vía aérea y vía ósea de cada oído.
      </p>
    </div>

  </div>
</template>

<script>
export default {
  name: "AyudaOtorrinolaringologia",
  data() {
    return {
      modos: [
        {
          icon: "brush",
          titulo: "Modo Edición",
          desc:   "Permite ingresar y modificar todos los datos del examen ORL. Solo disponible en atenciones PENDIENTE o PROCESO.",
        },
        {
          icon: "pageview",
          titulo: "Modo Consulta",
          desc:   "Solo lectura. Permite revisar los datos registrados sin modificarlos. Disponible en cualquier estado.",
        },
      ],
      tabs: [
        {
          icon: "hearing",
          titulo: "Oído general",
          desc:   "Lateralidad, tipo de consulta, otoscopia bilateral con estado de la membrana timpánica, acumetría con diapasón (Rinne, Weber, Schwabach) y síntomas auditivos.",
        },
        {
          icon: "graphic_eq",
          titulo: "Audiometría",
          desc:   "Audiometría tonal por vía aérea y vía ósea. Registra los umbrales en 6 frecuencias (250–8000 Hz) y calcula automáticamente el PTA con su clasificación.",
        },
        {
          icon: "waves",
          titulo: "Impedanciometría",
          desc:   "Timpanometría con tipo de timpanograma, presión de pico, compliancia y volumen del canal. Incluye reflejos estapediales en 4 frecuencias.",
        },
      ],
      otoscopia: [
        {
          icon: "search",
          titulo: "Membrana timpánica (OD / OI)",
          desc:   "Estado de la membrana de cada oído: íntegra, perforada, retráctil o abombada. Se registra de forma independiente para oído derecho e izquierdo.",
        },
        {
          icon: "text_snippet",
          titulo: "Descripción otoscópica",
          desc:   "Campo libre para describir los hallazgos visuales de cada oído al examen con otoscopio.",
        },
        {
          icon: "vibration",
          titulo: "Rinne (OD / OI)",
          desc:   "Prueba de diapasón que compara conducción aérea y ósea. Positivo: conducción aérea > ósea (normal o hipoacusia sensorioneural). Negativo: conducción ósea > aérea (hipoacusia conductiva).",
        },
        {
          icon: "center_focus_weak",
          titulo: "Weber",
          desc:   "Indica hacia qué lado lateraliza la vibración del diapasón en el vértice del cráneo. Ayuda a diferenciar el tipo y lado de la hipoacusia.",
        },
        {
          icon: "straighten",
          titulo: "Schwabach",
          desc:   "Compara la conducción ósea del paciente con la del examinador. Normal, alargado (hipoacusia conductiva) o acortado (hipoacusia sensorioneural).",
        },
      ],
      audiometria: [
        {
          icon: "swap_vert",
          titulo: "Vía aérea / Vía ósea",
          desc:   "Registra por separado los umbrales auditivos por vía aérea (VA) y vía ósea (VO) para determinar el tipo de hipoacusia.",
        },
        {
          icon: "table_chart",
          titulo: "Frecuencias (250–8000 Hz)",
          desc:   "Ingresa el umbral auditivo en dB para cada frecuencia. Los valores van de 0 a 120 dB.",
        },
        {
          icon: "calculate",
          titulo: "PTA automático",
          desc:   "El Promedio de Tonos Puros (PTA) se calcula automáticamente con los umbrales de 500, 1000, 2000 y 4000 Hz al ingresar cualquier valor. La clasificación (Normal / Leve / Moderada / Severa / Profunda) aparece como etiqueta.",
        },
      ],
      impedanciometria: [
        {
          icon: "show_chart",
          titulo: "Tipo de timpanograma",
          desc:   "Clasificación de Jerger del timpanograma: A (normal), As (rigidez), Ad (hipercompliance), B (derrame o perforación), C (presión negativa en oído medio).",
        },
        {
          icon: "compress",
          titulo: "Presión de pico (daPa)",
          desc:   "Presión del canal auditivo en el punto de máxima compliancia. Valores normales: entre -100 y +50 daPa.",
        },
        {
          icon: "water",
          titulo: "Compliancia (ml) y Volumen",
          desc:   "Compliancia: elasticidad del sistema tímpano-osicular. Volumen del canal: útil para detectar perforaciones timpánicas (valores elevados).",
        },
        {
          icon: "sensors",
          titulo: "Reflejos estapediales",
          desc:   "Presencia o ausencia del reflejo acústico en 500, 1000, 2000 y 4000 Hz. Su ausencia puede indicar patología del nervio facial o del oído medio.",
        },
      ],
      acciones: [
        {
          icon:   "add_circle",
          color:  "#0059cc",
          titulo: "Agregar registro",
          desc:   "Disponible en las pestañas Audiometría e Impedanciometría. Agrega una nueva fila para registrar datos de un oído adicional o una condición distinta.",
        },
        {
          icon:   "delete",
          color:  "#dc3545",
          titulo: "Eliminar registro",
          desc:   "Elimina la fila seleccionada. Solo visible en Modo Edición.",
        },
        {
          icon:   "restart_alt",
          color:  "#dc3545",
          titulo: "Limpiar",
          desc:   "Restablece todos los campos a sus valores iniciales y elimina los registros de audiometría e impedanciometría. Solo disponible en Modo Edición con atención PENDIENTE o PROCESO.",
        },
        {
          icon:   "save",
          color:  "#198754",
          titulo: "Guardar",
          desc:   "Persiste todos los datos de todas las pestañas en el servidor. Solo disponible en Modo Edición con atención PENDIENTE o PROCESO.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>